import { useMemo, useState } from 'react';
import { Stat } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatNumber, num, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

interface Item {
  id: string;
  kind: 'labor' | 'charge';
  date: string | null;
  workType: string;
  account?: string | null;
  classification?: string | null;
  contract?: string | null;
  contractType?: string | null;
  number?: string | null;
  link?: string | null;
  project?: string | null;
  itemTitle?: string | null;
  summary?: string | null;
  status?: string | null;
  source?: string | null;
  category?: string | null;
  issue?: string | null;
  subIssue?: string | null;
  role?: string | null;
  resource?: string | null;
  resourceId?: number | null;
  resourceActive?: boolean | null;
  hoursWorked?: number;
  billableHours?: number;
  nonBillableHours?: number;
  quantity?: number;
  rate: number | null;
  amount: number | null;
}
interface Group {
  workType: string;
  count: number;
  hoursWorked: number;
  billableHours: number;
  nonBillableHours: number;
  amount: number;
  items: Item[];
}
interface Payload {
  from: string;
  to: string;
  missing: { entity: string; reason: string }[];
  groups: Group[];
  totals: { count: number; labor: number; charges: number; hoursWorked: number; billableHours: number; nonBillableHours: number; amount: number };
  fetchedUtc: string;
}

const money = (v: number | null | undefined) => (v === null || v === undefined ? '—' : formatNumber(v, 'currency'));
const hrs = (v: number | undefined) => (v === undefined ? '' : num(v, 2));
const day = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' }) : '—');

function quick(k: 'week' | 'lastWeek' | 'month' | 'lastMonth'): { from: string; to: string } {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  if (k === 'week') return { from: toLocalDateString(monday), to: todayLocal() };
  if (k === 'lastWeek') {
    const s = new Date(monday);
    s.setDate(s.getDate() - 7);
    const e = new Date(monday);
    e.setDate(e.getDate() - 1);
    return { from: toLocalDateString(s), to: toLocalDateString(e) };
  }
  if (k === 'month') return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 1)), to: todayLocal() };
  return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth() - 1, 1)), to: toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 0)) };
}
const NONE = '__none__';
const QUICK: { k: Parameters<typeof quick>[0]; label: string }[] = [
  { k: 'week', label: 'This week' },
  { k: 'lastWeek', label: 'Last week' },
  { k: 'month', label: 'This month' },
  { k: 'lastMonth', label: 'Last month' },
];

/**
 * Reports › Pre-posting — Autotask's "Pre-posting details by work type",
 * live: everything entered in the days chosen that has not yet been approved
 * and posted — time entries and ticket, project and contract charges —
 * grouped by work type, each with its account, contract, ticket or task,
 * statuses, resource, hours, rate and billable amount
 * (routes/reportsPreposting.ts). Read once per range, kept until Refresh.
 */
export function PrepostingReportPage() {
  const [{ from, to }, setRange] = useState(() => quick('month'));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [search, setSearch] = useState('');
  /* '' is every contract type; NONE is items on no contract. */
  const [contractType, setContractType] = useState('');
  /* An Autotask resource id, or '' for everyone. */
  const [technician, setTechnician] = useState('');
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const data = useHeldResource<Payload>(`/reports/preposting?${new URLSearchParams({ from, to })}`);
  const d = data.data;

  const apply = (r: { from: string; to: string }) => {
    setDraftFrom(r.from);
    setDraftTo(r.to);
    setRange(r);
  };

  /* The contract types in this range, for the filter. */
  const contractTypes = useMemo(() => {
    const set = new Set<string>();
    let none = false;
    for (const g of d?.groups ?? []) for (const i of g.items) i.contractType ? set.add(i.contractType) : (none = true);
    return { types: [...set].sort((a, b) => a.localeCompare(b)), none };
  }, [d]);

  /* The technicians with time in this range — active ones first, and anyone
     Autotask has deactivated marked as such rather than left out. */
  const technicians = useMemo(() => {
    const byId = new Map<number, { name: string; active: boolean }>();
    for (const g of d?.groups ?? [])
      for (const i of g.items) if (i.resourceId && i.resource) byId.set(i.resourceId, { name: i.resource, active: i.resourceActive !== false });
    return [...byId]
      .map(([id, t]) => ({ id: String(id), ...t }))
      .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
  }, [d]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!d) return [];
    if (!q && !contractType && !technician) return d.groups;
    return d.groups
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (i) =>
            (!technician || String(i.resourceId) === technician) &&
            (!contractType || (contractType === NONE ? !i.contractType : i.contractType === contractType)) &&
            (!q ||
              [i.account, i.contract, i.contractType, i.number, i.project, i.itemTitle, i.summary, i.resource, i.role, i.status]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q))),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [d, search, contractType, technician]);

  /* Totals for what is shown, so the tiles and group heads follow the filters. */
  const sum = (list: Item[], k: 'hoursWorked' | 'billableHours' | 'nonBillableHours' | 'amount') => list.reduce((t, i) => t + (i[k] ?? 0), 0);
  const shown = useMemo(() => {
    const all = groups.flatMap((g) => g.items);
    return {
      count: all.length,
      labor: all.filter((i) => i.kind === 'labor').length,
      charges: all.filter((i) => i.kind === 'charge').length,
      hoursWorked: sum(all, 'hoursWorked'),
      billableHours: sum(all, 'billableHours'),
      nonBillableHours: sum(all, 'nonBillableHours'),
      amount: sum(all, 'amount'),
    };
  }, [groups]);

  const toggle = (k: string) =>
    setClosed((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  const exportCsv = () =>
    downloadCsv(`preposting_${from}_to_${to}_${fileStamp()}.csv`, [
      [
        'Work Type', 'Item Date', 'Classification', 'Contract', 'Contract Type', 'Account Name', 'Task or ticket number', 'Project Name', 'Item Title', 'Summary Note',
        'Task or Ticket Status', 'Source', 'Category', 'Issue', 'Sub-issue', 'Role Name', 'Resource Full Name', 'Hours worked', 'Quantity', 'Rate',
        'Actual Billable Hours', 'Actual Non-Billable Hours', 'Billable Amount',
      ],
      ...groups.flatMap((g) =>
        g.items.map((i) => [
          g.workType, i.date, i.classification, i.contract, i.contractType, i.account, i.number, i.project, i.itemTitle, i.summary,
          i.status, i.source, i.category, i.issue, i.subIssue, i.role, i.resource, i.hoursWorked ?? '', i.quantity ?? '', i.rate,
          i.billableHours ?? '', i.nonBillableHours ?? '', i.amount,
        ]),
      ),
    ]);

  return (
    <>
      <PageHead
        title="Pre-Posting & Ticket Detail"
        lede="Everything entered in Autotask that has not yet been approved and posted — time and charges — by work type, for the days you choose. Read live from Autotask."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Item date from">
              <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
            </Field>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!draftFrom || !draftTo || draftFrom > draftTo || (draftFrom === from && draftTo === to)}
              onClick={() => setRange({ from: draftFrom, to: draftTo })}
            >
              Show
            </button>
            <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
              {QUICK.map((q) => (
                <button key={q.k} type="button" className="btn btn-ghost btn-sm" onClick={() => apply(quick(q.k))}>
                  {q.label}
                </button>
              ))}
            </div>
            <Field label="Contract type">
              <select className="select" value={contractType} onChange={(e) => setContractType(e.target.value)}>
                <option value="">All contract types</option>
                {contractTypes.types.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
                {contractTypes.none && <option value={NONE}>No contract</option>}
                {contractType && contractType !== NONE && !contractTypes.types.includes(contractType) && <option value={contractType}>{contractType}</option>}
              </select>
            </Field>
            <Field label="Technician">
              <select className="select" value={technician} onChange={(e) => setTechnician(e.target.value)}>
                <option value="">All technicians</option>
                {technicians.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.active ? t.name : `${t.name} (inactive)`}
                  </option>
                ))}
                {technician && !technicians.some((t) => t.id === technician) && <option value={technician}>Not in these days</option>}
              </select>
            </Field>
            <Field label="Search">
              <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Account, ticket, resource, note" />
            </Field>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!groups.length} onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          <p className="xs muted mt-2">
            Time entries with no billing approval yet, and ticket, project and contract charges not yet billed, dated {day(from)} – {day(to)}. A time
            entry&apos;s rate is its role&apos;s hourly rate; the contract may rate it differently when it is posted.
            {technician && ' Charges have no technician in Autotask, so picking one shows their time entries only.'}
            {data.fetchedAt &&
              ` As of ${new Date(data.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
          </p>
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !d && <Loading label="Reading pre-posting items from Autotask" />}
      {d && d.missing.length > 0 && (
        <ErrorBanner message={d.missing.map((m) => `${m.entity} are not included: ${m.reason}`).join(' ')} />
      )}

      {d && (
        <>
          <div className="grid grid-4 mb-4">
            <Stat label="Items to post" value={num(shown.count)} foot={`${num(shown.labor)} time · ${num(shown.charges)} charges`} />
            <Stat label="Hours worked" value={num(shown.hoursWorked, 2)} foot={`${num(shown.billableHours, 2)} billable · ${num(shown.nonBillableHours, 2)} non-billable`} />
            <Stat label="Billable amount" value={money(shown.amount)} foot="time at role rates, plus charges" />
            <Stat label="Work types" value={num(groups.length)} foot="groups below" />
          </div>

          {groups.length === 0 ? (
            <section className="card">
              <div className="card-body">
                <p className="muted small">Nothing waiting to be posted{search || contractType || technician ? ' matches' : ' in these days'}.</p>
              </div>
            </section>
          ) : (
            groups.map((g) => {
              const isClosed = closed.has(g.workType);
              return (
                <section key={g.workType} className="card mb-4 pp-group">
                  <button type="button" className="pp-group-head" onClick={() => toggle(g.workType)} aria-expanded={!isClosed}>
                    <span className="pp-caret">{isClosed ? '▸' : '▾'}</span>
                    <strong>Work Type: {g.workType}</strong>
                    <span className="xs muted">
                      Total Count {num(g.items.length)} · {num(sum(g.items, 'hoursWorked'), 2)} h worked · {num(sum(g.items, 'billableHours'), 2)} billable ·{' '}
                      {money(sum(g.items, 'amount'))}
                    </span>
                  </button>
                  {!isClosed && (
                    <div className="table-wrap">
                      {/* Nine columns, fixed to the width of the page: the contract sits under the
                          account, status under the item, and the three hour figures in one cell, so
                          every column fits without scrolling sideways. */}
                      <table className="table pp-table">
                        <colgroup>
                          <col style={{ width: '8%' }} />
                          <col style={{ width: '13%' }} />
                          <col style={{ width: '10%' }} />
                          <col style={{ width: '17%' }} />
                          <col style={{ width: '19%' }} />
                          <col style={{ width: '10%' }} />
                          <col style={{ width: '8%' }} />
                          <col style={{ width: '7%' }} />
                          <col style={{ width: '8%' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Account / contract</th>
                            <th>Ticket / task</th>
                            <th>Item / status</th>
                            <th>Summary Note</th>
                            <th>Resource</th>
                            <th className="num">Hours</th>
                            <th className="num">Rate</th>
                            <th className="num">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.items.map((i) => (
                            <tr key={i.id}>
                              <td className="xs">{day(i.date)}</td>
                              <td>
                                {i.account ?? '—'}
                                {i.classification && <div className="xs muted">{i.classification}</div>}
                                {i.contract && <div className="xs muted">{i.contract}</div>}
                                {i.contractType && <div className="xs subtle">{i.contractType}</div>}
                              </td>
                              <td className="xs">
                                {i.number ? (
                                  i.link ? (
                                    <a href={i.link} target="_blank" rel="noreferrer">
                                      {i.number}
                                    </a>
                                  ) : (
                                    i.number
                                  )
                                ) : (
                                  '—'
                                )}
                                {i.project && <div className="muted">{i.project}</div>}
                              </td>
                              <td>
                                {i.itemTitle ?? '—'}
                                {i.kind === 'charge' && <span className="xs muted"> · charge{i.quantity ? ` × ${num(i.quantity)}` : ''}</span>}
                                {(i.issue || i.subIssue || i.category) && (
                                  <div className="xs muted">{[i.category, i.issue, i.subIssue].filter(Boolean).join(' › ')}</div>
                                )}
                                {(i.status || i.source) && <div className="xs muted">{[i.status, i.source].filter(Boolean).join(' · ')}</div>}
                              </td>
                              <td className="xs">
                                {i.summary ? (
                                  <span className="pp-note" title={i.summary}>
                                    {i.summary}
                                  </span>
                                ) : (
                                  <span className="subtle">—</span>
                                )}
                              </td>
                              <td className="xs">
                                {i.resource ?? '—'}
                                {i.role && <div className="muted">{i.role}</div>}
                              </td>
                              <td className="num">
                                {hrs(i.hoursWorked)}
                                {i.kind === 'labor' && (
                                  <div className="xs muted">
                                    {hrs(i.billableHours)} bill · {hrs(i.nonBillableHours)} non
                                  </div>
                                )}
                              </td>
                              <td className="num xs">{i.rate === null ? '—' : money(i.rate)}</td>
                              <td className="num strong">{money(i.amount)}</td>
                            </tr>
                          ))}
                          <tr className="cr-total">
                            <td colSpan={6}>Total Count {num(g.items.length)}</td>
                            <td className="num">
                              {num(g.items.reduce((t, i) => t + (i.hoursWorked ?? 0), 0), 2)}
                              <div className="xs muted">
                                {num(g.items.reduce((t, i) => t + (i.billableHours ?? 0), 0), 2)} bill ·{' '}
                                {num(g.items.reduce((t, i) => t + (i.nonBillableHours ?? 0), 0), 2)} non
                              </div>
                            </td>
                            <td />
                            <td className="num">{money(g.items.reduce((t, i) => t + (i.amount ?? 0), 0))}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              );
            })
          )}
        </>
      )}
    </>
  );
}
